/**
 * Orchestrator hook for the invoice upload phase.
 *
 * Manages two sub-phases:
 *   1. Upload  — per-file S3 uploads via useUploadQueue
 *   2. Trigger — POST attachment IDs to startBatchProcessing
 *
 * Once a batch is successfully started it is handed off to useBatchManager
 * (via onBatchStarted callback) and this hook resets to idle — making the
 * upload button immediately available for a new upload.
 *
 * "complete" phase is only reached when:
 *   a) Some files failed to upload (user needs to retry them), OR
 *   b) The startBatchProcessing API call itself failed (batchStartError set)
 *
 * OCR progress / results for running batches are owned by useBatchManager,
 * not by this hook.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useUploadQueue } from "./useUploadQueue";
import { startBatchProcessing, type BatchProcessContext } from "../api/attachmentApi";
import {
  FILE_UPLOAD_STATUS,
  DEFAULT_MAX_FILES,
  type FileUploadItem,
  type UploadQueueSummary,
  type FileValidationError,
  type AddFilesResult,
  type FlowPhase,
  type BatchFileSnapshot,
} from "../types/attachment";

// --- Phase derivation ---

function derivePhase(
  total: number,
  isUploading: boolean,
  started: boolean,
  batchPending: boolean,
  hasUploadFailures: boolean,
  batchStartError: string | null,
): FlowPhase {
  if (total === 0 && !started && !batchPending) return "idle";
  if (total > 0 && !started) return "selected";
  // While uploading OR while the batch-trigger API call is in-flight
  if (isUploading || batchPending) return "uploading";
  // Batch trigger failed — show error + retry option
  if (batchStartError) return "complete";
  // Some files didn't make it to S3 — show retry list
  if (started && hasUploadFailures) return "complete";
  // Remaining case: started && !hasUploadFailures — reset() was called, state is clearing
  return "idle";
}

// --- Hook ---

export type UseInvoiceUploadFlowReturn = {
  phase: FlowPhase;
  items: FileUploadItem[];
  summary: UploadQueueSummary;
  rejectedFiles: FileValidationError[];
  /** Set when startBatchProcessing API call itself failed (not an OCR failure). */
  batchStartError: string | null;
  /** Maximum number of files allowed for this upload context (1 for invoice-level, DEFAULT_MAX_FILES otherwise). */
  maxFiles: number;

  addFiles: (files: File[]) => AddFilesResult;
  startUpload: () => void;
  cancelFile: (index: number) => void;
  retryFile: (index: number) => void;
  retryAllFailed: () => void;
  retryBatchProcessing: () => void;
  cancelAll: () => void;
  dismissRejected: () => void;
  reset: () => void;
};

export function useInvoiceUploadFlow(opts: {
  /** Called once a batch is successfully started. Ownership transfers to useBatchManager. */
  onBatchStarted: (batchId: string, files: BatchFileSnapshot[]) => void;
  /** PO code to associate uploads with. Passed to startBatchProcessing as context. */
  poCode?: string;
  /** Invoice ID to associate uploads with (re-parse into existing invoice). */
  invoiceId?: number;
}): UseInvoiceUploadFlowReturn {
  const [started, setStarted] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState<FileValidationError[]>([]);
  /** True while the startBatchProcessing API call is in-flight. */
  const [batchPending, setBatchPending] = useState(false);
  const [batchStartError, setBatchStartError] = useState<string | null>(null);

  // Track which attachmentIds have already been sent to a batch so retried files
  // don't get submitted a second time in the same session.
  const batchedAttachmentIdsRef = useRef<Set<string>>(new Set());

  // Keep the callback ref fresh without needing it as a useCallback dependency.
  const onBatchStartedRef = useRef(opts.onBatchStarted);
  useEffect(() => {
    onBatchStartedRef.current = opts.onBatchStarted;
  });

  // Keep context refs fresh so triggerBatchProcessing never closes over stale values.
  const poCodeRef = useRef(opts.poCode);
  useEffect(() => { poCodeRef.current = opts.poCode; });
  const invoiceIdRef = useRef(opts.invoiceId);
  useEffect(() => { invoiceIdRef.current = opts.invoiceId; });

  const uploadQueue = useUploadQueue();
  const { items, summary, isUploading } = uploadQueue;

  const hasUploadFailures = items.some((i) => i.status === FILE_UPLOAD_STATUS.FAILED);
  const phase = derivePhase(summary.total, isUploading, started, batchPending, hasUploadFailures, batchStartError);

  // --- Trigger batch processing ---

  const triggerBatchProcessing = useCallback(() => {
    // Only include files that are uploaded AND not already sent to a previous batch
    const attachmentIds = items
      .filter(
        (i) =>
          i.status === FILE_UPLOAD_STATUS.UPLOADED &&
          i.attachmentId &&
          !batchedAttachmentIdsRef.current.has(i.attachmentId),
      )
      .map((i) => i.attachmentId!);

    if (attachmentIds.length === 0) return;

    setBatchStartError(null);
    setBatchPending(true);

    const context: BatchProcessContext = {
      poCode: poCodeRef.current,
      invoiceId: invoiceIdRef.current,
    };
    startBatchProcessing(attachmentIds, context)
      .then((response) => {
        const batchId = response.batch_id ?? response.workflow_id ?? null;
        if (!batchId) {
          setBatchStartError("No batch ID returned by server");
          setBatchPending(false);
          return;
        }

        // Build lightweight snapshot (no File references — safe to keep in state long-term)
        const filesSnapshot: BatchFileSnapshot[] = items
          .filter(
            (i) =>
              i.status === FILE_UPLOAD_STATUS.UPLOADED &&
              i.attachmentId &&
              !batchedAttachmentIdsRef.current.has(i.attachmentId),
          )
          .map((i) => ({ name: i.file.name, attachmentId: i.attachmentId! }));

        // Mark as batched BEFORE handing off
        filesSnapshot.forEach((f) => batchedAttachmentIdsRef.current.add(f.attachmentId));
        onBatchStartedRef.current(batchId, filesSnapshot);

        const currentHasFailures = items.some((i) => i.status === FILE_UPLOAD_STATUS.FAILED);
        if (currentHasFailures) {
          // Stay in "complete" so user can retry the failed uploads
          setBatchPending(false);
        } else {
          // Clean handoff — reset everything back to idle immediately.
          // React 18 batches all these updates into a single render.
          batchedAttachmentIdsRef.current = new Set();
          uploadQueue.reset();
          setStarted(false);
          setRejectedFiles([]);
          setBatchStartError(null);
          setBatchPending(false);
        }
      })
      .catch((err) => {
        setBatchStartError(err instanceof Error ? err.message : "Batch processing failed");
        setBatchPending(false);
      });
  }, [items, uploadQueue.reset]);

  // --- Upload → Trigger transition ---
  // Fires when the upload queue transitions from active → idle after user started uploading.

  const prevIsUploading = useRef(false);

  useEffect(() => {
    if (
      prevIsUploading.current &&
      !isUploading &&
      started &&
      !batchPending &&
      !batchStartError
    ) {
      triggerBatchProcessing();
    }
    prevIsUploading.current = isUploading;
  }, [isUploading, started, batchPending, batchStartError, triggerBatchProcessing]);

  // --- Actions ---

  const addFiles = useCallback(
    (files: File[]): AddFilesResult => {
      const currentMax = invoiceIdRef.current !== undefined ? 1 : DEFAULT_MAX_FILES;
      const available = Math.max(0, currentMax - items.length);
      const toAdd = files.slice(0, available);
      const overflow = files.slice(available).map((f) => ({
        file: f,
        reason: `Only ${currentMax} file${currentMax === 1 ? "" : "s"} allowed`,
      }));
      const result = toAdd.length > 0 ? uploadQueue.addFiles(toAdd) : { accepted: 0, rejected: [] };
      const allRejected = [...result.rejected, ...overflow];
      if (allRejected.length > 0) setRejectedFiles(allRejected);
      return { ...result, rejected: allRejected };
    },
    // items is needed so the remaining-slot count stays current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, uploadQueue.addFiles],
  );

  const startUpload = useCallback(() => {
    setStarted(true);
    setRejectedFiles([]);
    uploadQueue.startUpload();
  }, [uploadQueue.startUpload]);

  const retryBatchProcessing = useCallback(() => {
    triggerBatchProcessing();
  }, [triggerBatchProcessing]);

  const reset = useCallback(() => {
    uploadQueue.reset();
    setStarted(false);
    setRejectedFiles([]);
    setBatchPending(false);
    setBatchStartError(null);
    batchedAttachmentIdsRef.current = new Set();
  }, [uploadQueue.reset]);

  const maxFiles = opts.invoiceId !== undefined ? 1 : DEFAULT_MAX_FILES;

  return {
    phase,
    items,
    summary,
    rejectedFiles,
    batchStartError,
    maxFiles,

    addFiles,
    startUpload,
    cancelFile: uploadQueue.cancelFile,
    retryFile: uploadQueue.retryFile,
    retryAllFailed: uploadQueue.retryAllFailed,
    retryBatchProcessing,
    cancelAll: uploadQueue.cancelAll,
    dismissRejected: useCallback(() => setRejectedFiles([]), []),
    reset,
  };
}
